import { get, API_BASE_URL } from "../client";
import {
  CustomerProfile,
  PaginatedResponse,
  BookingRecord,
} from "../../../types";

interface ApiCustomer {
  id: string;
  email: string;
  phone: string;
  full_name: string;
  category: string;
  total_visits: number;
  total_spend: number | null;
  last_visit: string | null;
}

interface CustomersResponse {
  success: boolean;
  data: {
    data: ApiCustomer[];
    meta: {
      total: number;
      perPage: number;
      currentPage: number;
      lastPage: number;
    };
  };
}

interface CustomerDetailResponse {
  success: boolean;
  data: {
    customer: {
      id: string;
      email: string;
      phone: string;
      fullName: string;
      bookings: any[];
    };
    totalSpend: number;
    totalVisits: number;
  };
}

function transformCustomer(api: ApiCustomer): CustomerProfile {
  return {
    id: api.id,
    email: api.email,
    name: api.full_name,
    phone: api.phone,
    type: transformCategory(api.category),
    totalVisits: api.total_visits || 0,
    totalSpend: api.total_spend || 0,
    lastVisit: new Date(api.last_visit).toLocaleDateString("id-ID") || "-",
  };
}

function transformCategory(cat: string): "Personal" | "School" | "Agent" {
  const map: Record<string, "Personal" | "School" | "Agent"> = {
    personal: "Personal",
    school: "School",
    agent: "Agent",
  };
  return map[cat.toLowerCase()] || "Personal";
}
export async function getCustomers(
  page: number = 1,
  limit: number = 5,
  search: string = ""
): Promise<PaginatedResponse<CustomerProfile>> {
  const response = await get<CustomersResponse>("/admin/database", {
    page,
    limit,
    search,
  });

  return {
    data: response.data.data.map(transformCustomer),
    meta: {
      total: response.data.meta.total,
      perPage: response.data.meta.perPage,
      currentPage: response.data.meta.currentPage,
      lastPage: response.data.meta.lastPage,
      firstPage: 1,
    },
  };
}

export async function getCustomerHistory(
  idOrEmail: string
): Promise<BookingRecord[]> {
  const response = await get<CustomerDetailResponse>(
    `/admin/database/${idOrEmail}`
  );
  const bookings = response.data.customer?.bookings || [];
  return bookings.map((b: any) => ({
    id: b.id,
    code: b.bookingCode || b.booking_code,
    customerName: b.fullName || "",
    email: b.email || "",
    phone: b.phone || "",
    date: b.visitDate || b.visit_date,
    category:
      b.category === "school"
        ? "School"
        : b.category === "agent"
        ? "Agent"
        : "Personal",
    ticketId: b.ticketId || b.ticket_id,
    ticketType: b.ticket?.name || "",
    pax: b.participant,
    totalPrice: b.totalAmount || b.total_amount,
    status: (b.paymentStatus || b.payment_status || "").toUpperCase(),
    paymentMethod: (
      b.paymentMethod ||
      b.payment_method ||
      "QRIS"
    ).toUpperCase(),
    bookingDate: b.createdAt || b.created_at,
  }));
}

export async function exportCustomersToExcel(): Promise<void> {
  const token = localStorage.getItem("auth_token");
  const response = await fetch(`${API_BASE_URL}/admin/database/export`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Gagal mengexport data");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `database-pelanggan-${
    new Date().toISOString().split("T")[0]
  }.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
